import { AttributeOptions, IngressRequestForm } from '@dispatch/shared/types';
import { SubSection } from '@dispatch/web/ui';
import {
  Control,
  FieldValues,
  useFieldArray,
  UseFormGetValues,
  UseFormWatch,
} from 'react-hook-form';
import { Divider } from '@mui/material';
import { IngressClaimAttributesAdd } from './IngressClaimAttributesAdd';
import { IngressClaimAttributesForm } from './IngressClaimAttributesForm';
import type { IndexedField } from '../pinnedClaimAttributes';
import { partitionPinnedFields } from '../pinnedClaimAttributes';

export type IngressClaimAttributesSectionProps = {
  control: Control<FieldValues>;
  watch: UseFormWatch<IngressRequestForm>;
  getValues: UseFormGetValues<IngressRequestForm>;
  companyAttributes: AttributeOptions[];
  /** undefined while the tenant's pinned attributes are still resolving */
  pinnedAttributeGuids: readonly string[] | undefined;
  isReview?: boolean;
};

export function IngressClaimAttributesSection({
  control,
  watch,
  getValues,
  companyAttributes,
  pinnedAttributeGuids,
  isReview,
}: IngressClaimAttributesSectionProps) {
  const { fields, remove, append } = useFieldArray({
    control,
    name: 'claimAttributes',
  });

  const { pinned, others } = partitionPinnedFields(fields, (index) =>
    (pinnedAttributeGuids ?? []).includes(
      getValues(`claimAttributes.${index}.attributeDefinitionIntegrationGUID`)
    )
  );

  const renderRow =
    (isPinned: boolean) =>
    ({ field, index }: IndexedField<Record<'id', string>>) =>
      (
        <IngressClaimAttributesForm
          key={field.id}
          control={control}
          claimAttributeIndex={index}
          claimAttributeRemove={remove}
          field={field}
          getValues={getValues}
          companyAttributes={companyAttributes}
          isPinned={isPinned}
          isReview={isReview}
        />
      );

  return (
    <div
      style={{
        padding: '24px',
      }}
    >
      <SubSection title={'Claim Attributes'}>
        {pinned.map(renderRow(true))}
        <>
          <IngressClaimAttributesAdd
            append={append}
            control={control}
            watch={watch}
            companyAttributes={companyAttributes}
            pinnedAttributeGuids={pinnedAttributeGuids}
            isReview={isReview}
          />
          <Divider />
        </>

        {others.map(renderRow(false))}
      </SubSection>
    </div>
  );
}
